import Select, { StylesConfig } from 'react-select';

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  isDisabled?: boolean;
  theme?: 'light' | 'dark';
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  className = '',
  autoFocus = false,
  isDisabled = false,
  theme = 'light',
}) => {
  const selectedOption = options.find((opt) => opt.value === value) || null;

  const customStyles: StylesConfig<Option, false> = {
    control: (base, state) => ({
      ...base,
      minHeight: theme === 'dark' ? '44px' : '48px',
      padding: '0',
      fontSize: theme === 'dark' ? '1rem' : '1.1rem',
      border:
        theme === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.2)'
          : '2px solid rgba(38, 70, 83, 0.15)',
      borderRadius: '15px',
      background:
        theme === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(42, 157, 143, 0.03)',
      cursor: 'pointer',
      boxShadow: 'none',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: theme === 'dark' ? '#e9c46a' : '#2a9d8f',
      },
      ...(state.isFocused && {
        borderColor: theme === 'dark' ? '#e9c46a' : '#2a9d8f',
        background: theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'white',
        boxShadow:
          theme === 'dark'
            ? '0 0 0 3px rgba(233, 196, 106, 0.2)'
            : '0 0 0 3px rgba(42, 157, 143, 0.1)',
      }),
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0.5rem 1rem',
    }),
    input: (base) => ({
      ...base,
      color: theme === 'dark' ? 'white' : '#264653',
      margin: 0,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: theme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#9ca3af',
    }),
    singleValue: (base) => ({
      ...base,
      color: theme === 'dark' ? 'white' : '#264653',
      fontWeight: 500,
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '15px',
      overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(38, 70, 83, 0.25)',
      border:
        theme === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.2)'
          : '1px solid rgba(38, 70, 83, 0.1)',
      marginTop: '8px',
      zIndex: 9999,
    }),
    menuList: (base) => ({
      ...base,
      padding: '8px',
      background: theme === 'dark' ? '#264653' : 'white',
    }),
    option: (base, state) => ({
      ...base,
      padding: '12px 16px',
      fontSize: '1.05rem',
      borderRadius: '10px',
      cursor: 'pointer',
      background: state.isSelected
        ? theme === 'dark'
          ? 'rgba(233, 196, 106, 0.2)'
          : 'rgba(42, 157, 143, 0.15)'
        : state.isFocused
          ? theme === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(42, 157, 143, 0.08)'
          : 'transparent',
      color: state.isSelected
        ? theme === 'dark'
          ? '#e9c46a'
          : '#2a9d8f'
        : theme === 'dark'
          ? 'white'
          : '#264653',
      fontWeight: state.isSelected ? 600 : 500,
      transition: 'all 0.15s ease',
      '&:active': {
        background:
          theme === 'dark'
            ? 'rgba(233, 196, 106, 0.25)'
            : 'rgba(42, 157, 143, 0.2)',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: theme === 'dark' ? '#e9c46a' : '#2a9d8f',
      padding: '0 12px',
      transition: 'transform 0.2s ease',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0)',
      '&:hover': {
        color: theme === 'dark' ? '#f0d07a' : '#26867b',
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: theme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#9ca3af',
      padding: '0 8px',
      '&:hover': {
        color: theme === 'dark' ? '#e76f51' : '#e76f51',
      },
    }),
  };

  return (
    <Select<Option>
      value={selectedOption}
      onChange={(option) => onChange(option?.value || '')}
      options={options}
      styles={customStyles}
      placeholder={placeholder}
      className={className}
      autoFocus={autoFocus}
      isDisabled={isDisabled}
      isSearchable={false}
      menuPlacement="auto"
      menuPosition="absolute"
      menuPortalTarget={document.body}
    />
  );
};
